import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, List, Map } from 'immutable';
import { Observable, Subject } from 'rxjs';
import { CustomField } from '../../../settings-data/sub-models/custom-field.model';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import { AddAdditionalFieldsBuilder } from './add-additional-fields.builder';
import { filter, takeUntil } from 'rxjs/operators';

export enum AddAdditionalFieldsActionsEnum {
  close_form = 'close_form',
  show_form = 'show_form',
}

export interface IAddAdditionalFieldsActionTrigger {
  action: AddAdditionalFieldsActionsEnum;
}

@Component({
  selector: 'app-add-additional-fields',
  templateUrl: './add-additional-fields.component.html',
  styleUrls: ['./add-additional-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AddAdditionalFieldsBuilder],
})
export class AddAdditionalFieldsComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() additionalFields: List<CustomField>;
  @Input() actionTriggers: Observable<IAddAdditionalFieldsActionTrigger>;

  @Output() onUpdateAdditionalFields: EventEmitter<
    List<Map<string, any>>
  > = new EventEmitter<List<Map<string, any>>>();
  @Output() onEditMode: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _el: any;
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  form: FormGroup;
  formKeys: any;
  editMode: boolean;
  hasValue: boolean;

  constructor(
    private _elementRef: ElementRef,
    @Inject(DOCUMENT) private _document: Document,
    private _loggerService: LoggerService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _addAdditionalFieldsBuilder: AddAdditionalFieldsBuilder
  ) {}

  ngOnChanges(changes) {
    const additionalFieldsChanges = changes.additionalFields;

    if (additionalFieldsChanges) {
      const additionalFields = additionalFieldsChanges.currentValue;

      if (additionalFields) {
        this.form = this._addAdditionalFieldsBuilder.buildForm(
          additionalFields
        );
        this.hasValue = additionalFields.some(
          (field: CustomField) => !!field.value
        );
      }
    }
  }

  ngOnInit() {
    this._el = this._elementRef.nativeElement;

    this.actionTriggers
      .pipe(
        filter((actionTrigger: IAddAdditionalFieldsActionTrigger) => {
          return (
            this.editMode &&
            actionTrigger &&
            actionTrigger.action === AddAdditionalFieldsActionsEnum.close_form
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this.saveAdditionalFields());
    this.actionTriggers
      .pipe(
        filter((actionTrigger: IAddAdditionalFieldsActionTrigger) => {
          return (
            !this.editMode &&
            actionTrigger &&
            actionTrigger.action === AddAdditionalFieldsActionsEnum.show_form
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(() => this.toggleForm(true));
  }

  toggleForm(flag: boolean) {
    this.editMode = flag;
    this.onEditMode.emit(flag);

    if (flag) {
      setTimeout(() =>
        this._document.addEventListener(
          'click',
          this._handleClickOutsideCallback
        )
      );
    } else {
      this._document.removeEventListener(
        'click',
        this._handleClickOutsideCallback
      );

      this.hasValue = fromJS(this.form.value).some(
        (fieldValue: string) => !!fieldValue
      );
    }

    this._changeDetectorRef.markForCheck();
  }

  updateAdditionalFields(additionalFieldsInfo: Map<string, any>) {
    this.onUpdateAdditionalFields.emit(
      this._addAdditionalFieldsBuilder.buildCustomFieldsProps(
        this.additionalFields,
        additionalFieldsInfo
      )
    );
  }

  saveAdditionalFields() {
    this._loggerService.log('saving additional fields');

    this.updateAdditionalFields(fromJS(this.form.value));
    this.toggleForm(false);
  }

  getFormKeyForField(position: number) {
    return this._addAdditionalFieldsBuilder.buildFormKeyForField(position);
  }

  ngOnDestroy() {
    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _handleClickOutside(evt: Event) {
    const evtTarget = evt.target;

    if (!evtTarget) {
      return;
    }

    if (!this._el.contains(evtTarget)) {
      this.saveAdditionalFields();
    }
  }

  private _handleClickOutsideCallback = this._handleClickOutside.bind(this);
}
